import { Box, BraidPortal } from 'braid-design-system';
import clsx from 'clsx';

import type { JobActionMenuOption } from '../useJobActionMenuOptions';

import { DROPDOWN_MENU_CONTAINER } from './DropdownMenuContainer';
import { FixTabKeyToNavigate } from './FixTabKeyToNavigate';
import IconRow from './IconRow';
import { linkPropsWithKeyboardNavigation } from './buildHandleEnterAndSpaceKeyPress';

import * as styles from './dropdown.css';

// A workaround, where the value of the key was impacting the aria-label,
// possibly due to the options being updated after render.
// There is a future task to utilise the BRAID dropdown, which
// will hopefully fix this issue.
const randomKeyGenerator = () =>
  Math.floor(Math.random() * Math.floor(10000000000));

export const DropdownMenu = ({
  isOpen,
  onClick,
  options,
  toggleIsOpen,
  triggerRef,
  width,
}: {
  isOpen: boolean;
  onClick: (value: string) => void;
  options: JobActionMenuOption[];
  toggleIsOpen: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement> | null;
  width: number;
}) => {
  if (!isOpen) return null;

  const createValueChanged = (value: string) => () => {
    onClick(value);
    toggleIsOpen();
  };

  const { bottom: anchorBottom = 0, right: anchorRight = 0 } =
    triggerRef?.current?.getBoundingClientRect() || {};

  const alignMenuToBottomRightOfAnchor = {
    top: anchorBottom + window.scrollY,
    left: anchorRight - width,
  };

  const ClickOutSideToClose = () => (
    <Box
      className={styles.backdrop}
      zIndex="dropdownBackdrop"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        toggleIsOpen();
      }}
    />
  );

  return (
    <BraidPortal container={document.getElementById(DROPDOWN_MENU_CONTAINER)!}>
      <ClickOutSideToClose />

      <Box
        marginTop="small"
        role="navigation"
        zIndex="dropdown"
        style={{
          width,
          ...alignMenuToBottomRightOfAnchor,
        }}
        className={styles.options}
        data-testid="dropdown-menu"
      >
        <FixTabKeyToNavigate />

        {options.map(
          ({
            value,
            inactive,
            inactiveDescription,
            label: optionLabel,
            icon,
            content,
          }) => (
            <Box
              key={randomKeyGenerator()}
              width="full"
              paddingX="small"
              paddingY="small"
              aria-label={value}
              className={clsx(styles.option, {
                [styles.optionDisabled]: inactive,
              })}
              {...linkPropsWithKeyboardNavigation(createValueChanged(value))}
            >
              <IconRow
                key={value}
                icon={icon}
                label={optionLabel}
                onClick={() => createValueChanged(value)}
                inactive={inactive}
                inactiveDescription={inactiveDescription}
                content={content}
              />
            </Box>
          ),
        )}
      </Box>
    </BraidPortal>
  );
};
